import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  isPlatformServer,
  DOCUMENT,
  isPlatformBrowser,
  NgClass,
  NgIf,
  NgStyle,
} from '@angular/common';
import { finalize, Subscription } from 'rxjs';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import { ApiService } from '../../../services/api.service';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { SectionHeadingComponent } from '../section-heading/section-heading.component';
import { SectionDescriptionComponent } from '../section-description/section-description.component';
import { CardChipsComponent } from '../card/card-chips/card-chips.component';
import { CardSkeletonLoaderComponent } from '../../utility/card-skeleton-loader/card-skeleton-loader.component';
import { routes } from '../../../constants/routes';
import { CategoryTypes } from '../../../models/category-types.model';
import { CardType } from '../../../models/card-type.model';
import { CouldntLoadDataComponent } from '../couldnt-load-data/couldnt-load-data.component';
import { NoRecordFoundComponent } from '../no-record-found/no-record-found.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { ToasterService } from '../../../services/toaster.service';
import { Toolkit } from '../../../models/toolkit.model';
import { ToolkitCardComponent } from '../card/toolkit-card/toolkit-card.component';
import { UtilityService } from '../../../services/utility.service';

@Component({
  selector: 'app-home-toolkits',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    SectionContainerComponent,
    SectionHeadingComponent,
    SectionDescriptionComponent,
    CardChipsComponent,
    NgClass,
    CardSkeletonLoaderComponent,
    CouldntLoadDataComponent,
    NoRecordFoundComponent,
    ToolkitCardComponent,
    NgStyle
  ],
  templateUrl: './home-toolkits.component.html',
  styleUrl: './home-toolkits.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeToolkitsComponent
  implements OnInit, OnDestroy, ApiErrorHandler
{
  toolkits?: Toolkit[];
  isToolkitsLoading!: boolean;
  isErrorFetchingToolkits: boolean = false;

  readonly categoryTypes = CategoryTypes;
  public selectedCategory = this.categoryTypes.trending;
  readonly CardType = CardType;
  readonly routes = routes;
  description =
    'Empower your AI journey with essential toolkits. Access resources, templates, and guides to accelerate development and streamline workflows.';
  appliedFilters: any = null;
  sortBy = 'createdAt:ASC';

  isSwiperInitialized = false;

  private fetchToolkitsSubs!: Subscription;

  @ViewChild('swiperContainerToolkits')
  private swiperContainer!: ElementRef<any>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    public toaster: ToasterService,
    private apiService: ApiService,
    private utilityService: UtilityService
  ) {}

  ngOnInit() {
    this.selectedCategory = this.categoryTypes.trending;
    this.fetchToolkits();
  }

  public fetchToolkits() {
    this.toolkits = [];
    this.isToolkitsLoading = true;
    let filterParams;
    if (this.selectedCategory === this.categoryTypes.trending) {
      this.sortBy = 'createdAt:DESC';
      this.fetchTrendingUsecases();
      return;
    } else if (this.selectedCategory === this.categoryTypes.new) {
      this.sortBy = 'createdAt:ASC';
    }
    filterParams = this.utilityService
      .getFiltersParams(this.appliedFilters)
      .set('sort', this.sortBy);
    this.fetchToolkitsSubs = this.apiService
      .fetchtoolKits(1, 10, filterParams)
      .pipe(
        finalize(() => {
          this.isToolkitsLoading = false;
        })
      )
      .subscribe({
        next: (resp) => {
          this.toolkits = resp.data.toolkits;
          if (
            this.toolkits?.length &&
            !this.isSwiperInitialized &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            this.swiperContainer.nativeElement?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingToolkits = true;
          apiErrorHandler.call(this, error);
        },
      });
  }

  fetchTrendingUsecases() {
    this.fetchToolkitsSubs = this.apiService
      .fetchFilteredToolkit(this.selectedCategory)
      .pipe(
        finalize(() => {
          this.isToolkitsLoading = false;
        })
      )
      .subscribe({
        next: (resp) => {
          this.toolkits = resp.data;

          if (
            this.toolkits?.length &&
            !this.isSwiperInitialized &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            this.swiperContainer.nativeElement?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingToolkits = true;
          apiErrorHandler.call(this, error);
        },
      });
  }

  private initSwiperCarousel() {
    this.isSwiperInitialized = true;
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const swiperProps: SwiperOptions = {
      slidesPerView: 1.2,
      speed: 500,
      spaceBetween: 18,
      navigation: {
        prevEl: '.toolkit-carousel-arrow--prev',
        nextEl: '.toolkit-carousel-arrow--next',
      },
      loop: true,
      autoplay: {
        delay: 10000,
        stopOnLastSlide: false,
      },
      pagination: {
        el: '.toolkit-swiper-pagination ',
        clickable: true,
      },
      breakpoints: {
        1300: {
          slidesPerView: 4,
          spaceBetween: 18,
          // enabled: this.datasetDetails?.cards?.length > 4,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.toolkits!.length < 4) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
        1000: {
          slidesPerView: 3,
          spaceBetween: 18,
          // enabled: this.datasetDetails?.cards?.length > 3,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.toolkits!.length < 3) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 18,
          // enabled: this.datasetDetails?.cards?.length > 2,
          on: {
            init: (swiper) => {
              const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
              if (swiperWrapper && this.toolkits!.length < 2) {
                swiperWrapper.setAttribute('style', 'justify-content: center');
              }
            },
          },
        },
      },
    };

    Object.assign(this.swiperContainer.nativeElement, swiperProps);
    (<SwiperContainer>this.swiperContainer.nativeElement).initialize();
  }

  ngOnDestroy() {
    this.fetchToolkitsSubs.unsubscribe();
  }
}
